import { useQuery } from "@apollo/client";

import Table from "@/components/Table";
import Tabs from "@/components/Tabs";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import AppLayout from "@/layouts/AppLayout";
import { ALL_SECTIONS_QUERY } from "./queries";
import { Section } from "@/types";
import usePageTitle from "@/hooks/usePageTitle";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import useCapabilities, { DOMAIN, PERMISSION, useHasRoles, useRequireCapability } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import SectionsCatalog from "./_components/SectionsCatalog";

const pattern = "/sections";

export default function SectionManagement() {
  useRequireCapability({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewPeople });

  usePageTitle(pattern, "Sections");

  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const { loaded: authLoaded } = useCapabilities();
  const isTechnologyStaff = useHasRoles([ROLE.TECHNOLOGY_STAFF, ROLE.GLOBAL_ADMIN]);
  const gateReady = flagsLoaded && authLoaded;
  const sectionsCatalogEnabled = gateReady && !!flags["backoffice-sections-catalog"] && isTechnologyStaff;
  const { data, loading } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY);
  const sections = data?.sections ?? [];

  const legacyTable = (
    <Table
      loading={loading || !gateReady}
      getRowLink={(row) => ({
        to: "/sections/:sectionId",
        params: { sectionId: row.original.id }
      })}
      data={sections}
      columns={[
        {
          accessorKey: "name",
          header: "Name",
          enableSorting: false,
          meta: {
            className: getStickyColumnClasses(sections.length > 0)
          }
        }
      ]}
    />
  );

  return (
    <AppLayout bottomBar>
      <Tabs
        tabItems={[
          {
            active: true,
            label: "Sections"
          }
        ]}
      />
      <div className="pt-12" />
      {!gateReady ? (
        legacyTable
      ) : sectionsCatalogEnabled ? (
        <SectionsCatalog sections={sections} loading={loading} />
      ) : (
        legacyTable
      )}
    </AppLayout>
  );
}
